import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const AboutSection: React.FC = () => {
  const container = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    // Animate image from left
    gsap.from(imageRef.current, {
      x: -60,
      opacity: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: container.current,
        start: 'top 80%',
      }
    });

    // Animate text from right
    gsap.from(textRef.current, {
      x: 60,
      opacity: 0,
      duration: 1.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: container.current,
        start: 'top 80%',
      }
    });

    // Animate badges
    gsap.from('.stat-badge', {
      scale: 0.8,
      opacity: 0,
      duration: 0.6,
      stagger: 0.2,
      delay: 0.5,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: container.current,
        start: 'top 70%',
      }
    });
  }, { scope: container });

  return (
    <section ref={container} id="about" className="py-20 bg-earth-50 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div ref={imageRef} className="lg:w-1/2 relative">
            <div className="absolute top-4 left-4 w-full h-full border-2 border-turmeric-500 rounded-2xl z-0"></div>
            <img 
              src="https://res.cloudinary.com/djnukb8xv/image/upload/v1768230826/agelife_2_udyqz1.png" 
              alt="Traditional herbal preparation" 
              className="rounded-2xl shadow-lg relative z-10 w-full object-cover"
            />
          </div>
          
          <div ref={textRef} className="lg:w-1/2">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-herbal-900 mb-6">
              The Essence of Jamu Tradition
            </h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                At <span className="font-bold text-herbal-800">Age Life</span>, we bring you the ancient healing secrets of Indonesia. <strong>Jamu</strong> is a centuries-old herbal medicine tradition that utilizes roots, bark, flowers, seeds, leaves, and fruits to restore balance and vitality.
              </p>
              <p>
                Unlike synthetic alternatives, our remedies are 100% natural, honoring the body's innate ability to heal. Every product is rigorously tested and certified with <strong>GMP, ISO, and HALAL</strong> standards to ensure safety, authenticity, and efficacy.
              </p>
              <p>
                From the bustling streets of Nairobi to the hills of Kigali, we are dedicated to delivering holistic wellness across East Africa. Whether you seek pain relief, immune support, or metabolic balance, nature has a solution.
              </p>
            </div>
            
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="stat-badge bg-white p-4 rounded-lg shadow-sm border border-earth-200">
                <p className="text-3xl font-bold text-turmeric-600">100%</p>
                <p className="text-sm font-medium text-gray-600">Natural Ingredients</p>
              </div>
              <div className="stat-badge bg-white p-4 rounded-lg shadow-sm border border-earth-200">
                <p className="text-3xl font-bold text-turmeric-600">6+</p>
                <p className="text-sm font-medium text-gray-600">Countries Served</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};